/* eslint-disable react-hooks/exhaustive-deps */

import dynamic from 'next/dynamic';

import React, { Fragment, useState } from "react";
const Icon = dynamic(() => import('@/components/ui/Icon'), { ssr: false });
import Link from "next/link";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/custom/ui/dropdown-menu";
import {
  getMessages,
  setMessageSeen,
} from "@/utils/firebaseFunctions";
import { formatRelativeTime } from "@/utils/generalutils";
import { useAuth } from "@/contexts/authContext";

const notifyButton = (isLoading, messages) => {
  return (
    <span className="relative inline-flex justify-center btn btn-dark border-2 border-metaxseed-gray bg-metaxseed-dark-gray text-base sm:text-lg font-normal rounded-full px-2 py-2 md:px-4 md:py-2 focus:outline-none !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none shadow-sm">
      {(!isLoading) && (messages.filter((item) => item.is_seen === false).length > 0) && (
        <Fragment>
          <Icon icon="heroicons-outline:bell" className="animate-tada w-4 h-4 md:w-5 md:h-5" />
          <span className="absolute right-1 top-1 md:right-2 md:top-2 h-3 w-3 md:h-4 md:w-4 bg-metaxseed-orange text-[8px] md:text-[10px] font-semibold flex flex-col gothamMedium items-center justify-center rounded-full text-white z-[99]">
            {messages.filter((item) => item.is_seen === false).length}
          </span>
        </Fragment>
      )}

      {(!isLoading && messages.length > 0) && (messages.filter((item) => item.is_seen === false).length === 0) && (
        <Fragment>
          <Icon icon="heroicons-outline:bell" className="w-4 h-4 md:w-5 md:h-5" />
        </Fragment>
      )}

      {(messages.length === 0) && (
        <Fragment>
          <Icon icon="heroicons-outline:bell" className="w-4 h-4 md:w-5 md:h-5" />
        </Fragment>
      )}
    </span>
  );
};

const Notification = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const { walletAddress, activeConnector, user } = useAuth();

  const fetchMessages = async () => {
    setLoading(true);
    const messages = await getMessages(user?.user_id, 3);
    setMessages(messages);
    setLoading(false);
  };

  const markMessageAsSeen = async (messageId) => {
    try {
      // Mark message as seen in the database
      await setMessageSeen(messageId, user?.user_id || user?.uid, true);
      
      // Update local state to immediately reflect the change
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.id === messageId ? { ...msg, is_seen: true } : msg
        )
      );
    } catch (error) {
      console.error('Error marking message as seen:', error);
    }
  };

  const markAllMessagesAsSeen = async () => {
    try {
      const unseenMessages = messages.filter(msg => !msg.is_seen);
      
      // Mark all unseen messages as seen in the database
      await Promise.all(
        unseenMessages.map(msg => 
          setMessageSeen(msg.id, user?.user_id || user?.uid, true)
        )
      );
      
      // Update local state
      setMessages(prevMessages => 
        prevMessages.map(msg => ({ ...msg, is_seen: true }))
      );
    } catch (error) {
      console.error('Error marking all messages as seen:', error);
    }
  };

  React.useEffect(() => {
    if (walletAddress) {
      fetchMessages();
    } else {
      window.location.href = "/";
    }
  }, [walletAddress, activeConnector]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {notifyButton(loading, messages)}
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        className="w-[calc(100vw-2rem)] max-w-[320px] md:w-[300px] gothamBook bg-metaxseed-black border-2 border-metaxseed-gray"
        align="end"
        sideOffset={30}
        collisionPadding={16}
        avoidCollisions={true}
      >
        <div className="flex items-center justify-between gothamMedium px-4 py-4 border-b border-slate-600">
          <DropdownMenuLabel className="text-sm text-white font-medium leading-6 px-0 py-0">
            Messages
          </DropdownMenuLabel>
          <div className="flex items-center space-x-2 text-white text-xs">
            {messages.some(msg => !msg.is_seen) && (
              <button 
                onClick={markAllMessagesAsSeen}
                className="underline hover:text-metaxseed-orange"
              >
                Mark all read
              </button>
            )}
            {messages.length > 0 && (
              <Link href="/messages" className="underline">
                View all
              </Link>
            )}
          </div>
        </div>
        
        {messages.length === 0 && (
          <DropdownMenuItem className="block w-full px-4 py-2 text-sm cursor-default">
            <div className="flex">
              <div className="flex-1">
                <div className="text-metaxseed-text-gray text-xs leading-4">
                  There is no new message.
                </div>
              </div>
            </div>
          </DropdownMenuItem>
        )}

        {messages.slice(0, 3).map((item, i) => (
          <Fragment key={i}>
            <DropdownMenuItem
              className="block w-full px-4 py-2 text-sm cursor-pointer hover:bg-zinc-700 hover:bg-opacity-70 text-zinc-600 dark:text-zinc-300"
              onClick={() => {
                // Mark this specific message as seen
                if (!item.is_seen) {
                  markMessageAsSeen(item.id);
                }
                // Navigate to messages page
                window.location.href = "/messages";
              }}
            >
              <div className="flex ltr:text-left rtl:text-right">
                <div className="flex-none ltr:mr-3 rtl:ml-3">
                  <div className="h-8 w-8 rounded-full">
                    <img
                      src="https://firebasestorage.googleapis.com/v0/b/prodmxs.appspot.com/o/branding%2Fmxs_games%2Fv1%2Fwhite%2Fpng%2Fmxs_icon_white.png?alt=media&token=8a7e36ba-954b-46b3-bcb2-559cec885bdf"
                      alt=""
                      className="block w-full h-full object-cover rounded-full border border-transparent"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="text-zinc-600 dark:text-zinc-300 text-sm">
                    {item.title.length > 16 ? item.title.substring(0, 16) + "..." : item.title}
                  </div>
                  <div
                    className="text-zinc-600 dark:text-zinc-300 text-xs leading-4 max-h-12 overflow-hidden"
                    dangerouslySetInnerHTML={{ __html: item.message }}
                  />
                  <div className="text-zinc-400 dark:text-zinc-400 text-xs mt-1">
                    {formatRelativeTime(item?.created_at)}
                  </div>
                </div>
                {!item.is_seen && (
                  <div className="flex-0">
                    <span className="h-[10px] w-[10px] bg-metaxseed-orange border border-white dark:border-slate-400 rounded-full inline-block"></span>
                  </div>
                )}
              </div>
            </DropdownMenuItem>
            {i < messages.slice(0, 3).length - 1 && (
              <DropdownMenuSeparator className="bg-slate-800" />
            )}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default Notification;
